import { trackPageView } from '@repo/analytics/src/ga';
import { useEffect, useRef } from 'react';
import { isRouteErrorResponse, Outlet, useLocation, useRouteError } from 'react-router';

import ConsentBanner from '@/components/ConsentBanner';
import DashboardLoader from '@/components/shared/DashboardLoader';
import { useAppDispatch } from '@/store';
import { fetchDashboardConfig } from '@/store/dashboard';

const RootLayout = () => {
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const isInitialMount = useRef(true);

  // load app-level config once on boot; available to every page (incl. pre-auth)
  useEffect(() => {
    dispatch(fetchDashboardConfig());
  }, [dispatch]);

  useEffect(() => {
    trackPageView(pathname);
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    window.scrollTo(0, 0);
    // Close the off-canvas sidebar drawer after navigating on mobile.
    if (window.innerWidth < 1025) {
      document.body.classList.add('enlarge-menu', 'enlarge-menu-all');
    }
  }, [pathname]);

  return (
    <>
      <Outlet />
      <ConsentBanner />
    </>
  );
};

// Shown on the initial hard-load of a lazy route chunk.
export const HydrateFallback = () => <DashboardLoader />;

// Catches render/lazy-load errors anywhere in the tree (there was none before).
export function ErrorBoundary() {
  const error = useRouteError();
  const detail = isRouteErrorResponse(error) ? `${error.status} ${error.statusText}` : 'An unexpected error occurred.';
  return (
    <div className="d-flex flex-column align-items-center justify-content-center min-vh-100 text-center p-4">
      <h1 className="h4 mb-2">Something went wrong</h1>
      <p className="text-muted mb-3">{detail}</p>
      <a href="/" className="btn btn-primary">
        Go to dashboard
      </a>
    </div>
  );
}

export default RootLayout;
